import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { formatEuro, calculateVatBreakdown } from '../utils/format';
import {
  ShoppingBag,
  X,
  Plus,
  Minus,
  Trash2,
  Send,
  Sparkles,
  AlertCircle,
  Tag,
  Percent,
  Check,
  ChevronRight
} from 'lucide-react';
import confetti from 'canvas-confetti';

export const CartDrawer: React.FC = () => {
  const {
    cart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartSubtotal,
    cartTotal,
    cartItemCount,
    isCartDrawerOpen,
    setIsCartDrawerOpen,
    currentTable,
    customerProfile,
    setIsCustomerWelcomeOpen,
    submitCustomerOrder,
    coupons,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    couponDiscountAmount
  } = useApp();

  const { lang, t } = useLanguage();
  const [specialNotes, setSpecialNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponMessage, setCouponMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCouponsList, setShowCouponsList] = useState(false);

  // VAT calculations based on cart items
  const vatItems = cart.map(c => ({
    price: c.unit_price,
    quantity: c.quantity,
    vat_rate: c.menu_item.vat_rate
  }));
  const vat = calculateVatBreakdown(vatItems);

  const handleApplyCoupon = (codeToApply?: string) => {
    const code = codeToApply || couponInput;
    if (!code.trim()) return;

    const res = applyCoupon(code);
    if (res.success) {
      setCouponMessage({ type: 'success', text: res.message });
      setCouponInput('');
      setShowCouponsList(false);
    } else {
      setCouponMessage({ type: 'error', text: res.message });
    }
  };

  const handleCheckout = async () => {
    if (!customerProfile) {
      setIsCartDrawerOpen(false);
      setIsCustomerWelcomeOpen(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await submitCustomerOrder(specialNotes);

      // Trigger festive confetti
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (e: any) {
      alert(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Floating Bottom Pill if drawer is closed and items exist
  if (!isCartDrawerOpen) {
    if (cartItemCount === 0) return null;
    return (
      <div
        className="floating-cart-bar no-print"
        onClick={() => setIsCartDrawerOpen(true)}
      >
        <div className="cart-bar-left">
          <div className="cart-count-badge">{cartItemCount}</div>
          <div>
            <div className="cart-bar-title">{t('viewCart')}</div>
            <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>
              {t('table')} #{currentTable?.table_number || 1}
            </div>
          </div>
        </div>
        <div className="cart-bar-price">{formatEuro(cartTotal)}</div>
      </div>
    );
  }

  // Open Drawer View
  return (
    <div className="modal-overlay" onClick={() => setIsCartDrawerOpen(false)} style={{ zIndex: 108 }}>
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '500px', height: '90vh' }}
      >
        {/* Drawer Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--accent-copper-light)',
                color: 'var(--accent-copper)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ShoppingBag size={20} />
            </div>
            <div>
              <div className="modal-title">{t('cartTitle')}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--accent-amber)', fontWeight: 700 }}>
                {t('table')} #{currentTable?.table_number || 1}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}
                title="Tyhjennä ostoskori"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button className="modal-close-btn" onClick={() => setIsCartDrawerOpen(false)}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {cart.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <div style={{ fontSize: '48px' }}>🍕</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{t('cartEmpty')}</p>
            </div>
          ) : (
            <>
              {/* Items List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {cart.map(item => {
                  const name = lang === 'en' ? item.menu_item.name_en : lang === 'sv' ? item.menu_item.name_sv : item.menu_item.name_fi;
                  return (
                    <div
                      key={item.cart_item_id}
                      style={{
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 'var(--radius-md)',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{name}</div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--accent-copper)', fontWeight: 700 }}>
                            {formatEuro(item.unit_price)}
                          </div>
                        </div>
                        <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)' }}>
                          {formatEuro(item.total_price)}
                        </div>
                      </div>

                      {/* Modifiers */}
                      {item.selected_variants && item.selected_variants.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                          {item.selected_variants.map((v, i) => (
                            <div key={i}>
                              • {v.option_name} {v.price > 0 && `(+${formatEuro(v.price)})`}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Special instructions */}
                      {item.special_instructions && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--accent-amber)', fontStyle: 'italic' }}>
                          "{item.special_instructions}"
                        </div>
                      )}

                      {/* Quantity stepper */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: 'var(--bg-surface)',
                            borderRadius: 'var(--radius-full)',
                            padding: '2px'
                          }}
                        >
                          <button
                            onClick={() => updateCartQuantity(item.cart_item_id, item.quantity - 1)}
                            style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--bg-card)' }}
                          >
                            <Minus size={13} />
                          </button>
                          <span style={{ minWidth: '28px', textAlign: 'center', fontWeight: 800, fontSize: '0.85rem' }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateCartQuantity(item.cart_item_id, item.quantity + 1)}
                            style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--bg-card)' }}
                          >
                            <Plus size={13} />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.cart_item_id)}
                          style={{ color: 'var(--accent-rose)', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                          Poista
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* COUPON & PROMO CODE SECTION */}
              <div
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-md)',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 800, fontSize: '0.88rem' }}>
                    <Tag size={16} color="var(--accent-copper)" />
                    <span>{lang === 'en' ? 'Coupons & Offers' : 'Kupongit & Alennukset'}</span>
                  </div>

                  {!appliedCoupon && coupons.length > 0 && (
                    <button
                      onClick={() => setShowCouponsList(!showCouponsList)}
                      style={{ color: 'var(--accent-copper)', fontSize: '0.78rem', fontWeight: 700 }}
                    >
                      {showCouponsList ? 'Piilota' : 'Selaa kuponkeja'}
                    </button>
                  )}
                </div>

                {/* Applied Coupon Banner */}
                {appliedCoupon ? (
                  <div
                    style={{
                      background: 'rgba(16, 185, 129, 0.12)',
                      border: '1px solid var(--accent-emerald)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '10px 12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, color: 'var(--accent-emerald)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Check size={16} />
                        <span>Kuponki "{appliedCoupon.code}" aktivoitu!</span>
                      </div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                        {appliedCoupon.title} (-{formatEuro(couponDiscountAmount)})
                      </div>
                    </div>

                    <button
                      onClick={removeCoupon}
                      style={{ color: 'var(--accent-rose)', fontSize: '0.75rem', fontWeight: 700 }}
                    >
                      Poista
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Coupon Input Row */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder={lang === 'en' ? 'Enter coupon code (e.g. LUNCH1490)' : 'Syötä koodi (esim. LUNCH1490)'}
                        className="form-input"
                        style={{ textTransform: 'uppercase', fontSize: '0.82rem', padding: '8px 12px' }}
                        value={couponInput}
                        onChange={e => setCouponInput(e.target.value)}
                      />
                      <button
                        className="btn-primary"
                        style={{ padding: '8px 14px', fontSize: '0.82rem' }}
                        onClick={() => handleApplyCoupon()}
                      >
                        {lang === 'en' ? 'Apply' : 'Käytä'}
                      </button>
                    </div>

                    {couponMessage && (
                      <div
                        style={{
                          fontSize: '0.78rem',
                          color: couponMessage.type === 'success' ? 'var(--accent-emerald)' : 'var(--accent-rose)',
                          fontWeight: 600
                        }}
                      >
                        {couponMessage.text}
                      </div>
                    )}

                    {/* Quick Available Coupons List */}
                    {showCouponsList && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
                        {coupons.filter(c => c.is_active).map(cpn => (
                          <div
                            key={cpn.id}
                            style={{
                              background: 'var(--bg-surface)',
                              border: '1px dashed var(--border-medium)',
                              borderRadius: 'var(--radius-sm)',
                              padding: '8px 10px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--accent-copper)' }}>
                                {cpn.code} • {cpn.discount_type === 'percentage' ? `-${cpn.discount_value}%` : `-${formatEuro(cpn.discount_value)}`}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                {cpn.description}
                              </div>
                            </div>

                            <button
                              className="btn-secondary"
                              style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: 800 }}
                              onClick={() => handleApplyCoupon(cpn.code)}
                            >
                              Käytä
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Order Special Notes */}
              <div className="form-group">
                <label className="form-label">{t('tableNoteLabel')}</label>
                <textarea
                  className="form-input"
                  style={{ minHeight: '54px', fontSize: '0.82rem' }}
                  placeholder="Esim. Tuokaa annokset yhtä aikaa..."
                  value={specialNotes}
                  onChange={e => setSpecialNotes(e.target.value)}
                />
              </div>

              {/* Bill & VAT Breakdown Card */}
              <div className="vat-breakdown-card">
                <div className="vat-row">
                  <span>{t('subtotalNet')}</span>
                  <span>{formatEuro(cartSubtotal)}</span>
                </div>

                {couponDiscountAmount > 0 && (
                  <div className="vat-row" style={{ color: 'var(--accent-emerald)', fontWeight: 700 }}>
                    <span>Alennus (Kuponki {appliedCoupon?.code})</span>
                    <span>-{formatEuro(couponDiscountAmount)}</span>
                  </div>
                )}

                {vat.foodVat > 0 && (
                  <div className="vat-row">
                    <span>{t('vatFood')}</span>
                    <span>{formatEuro(vat.foodVat)}</span>
                  </div>
                )}

                {vat.beverageVat > 0 && (
                  <div className="vat-row">
                    <span>{t('vatBeverage')}</span>
                    <span>{formatEuro(vat.beverageVat)}</span>
                  </div>
                )}

                <div className="vat-row total">
                  <span>{t('totalToPay')}</span>
                  <span style={{ color: 'var(--accent-copper)', fontSize: '1.2rem' }}>
                    {formatEuro(cartTotal)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Drawer Footer */}
        {cart.length > 0 && (
          <div className="modal-footer">
            <button
              className="btn-primary"
              disabled={isSubmitting}
              onClick={handleCheckout}
              style={{ width: '100%', padding: '14px', fontSize: '1rem', justifyContent: 'center' }}
            >
              <Send size={18} />
              <span>{isSubmitting ? 'Lähetetään...' : t('confirmAndOrder')}</span>
              <span>•</span>
              <span>{formatEuro(cartTotal)}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
